/**
 * Contexte de la capacité « ouvrir un autre document », consommée par le renderer `a` du
 * markdown (SAAS-1163).
 *
 * @remarks
 * `MediaLink` est le renderer `a` **unique** — react-markdown n'en accepte qu'un par rendu — et
 * il vit dans `shared/` parce que `tech-tests` (rendu candidat, `PhaseDocPane`) et `admin`
 * (prévisualisation, `DocumentPreviewModal`) le partagent sans pouvoir s'importer entre eux.
 *
 * Or « résoudre » et « ouvrir » n'ont pas la même implémentation d'un côté et de l'autre :
 * `tech-tests` sélectionne le document dans le workspace (`setSelectedDocument`) parmi les
 * documents de la variante courante, tandis qu'`admin` rebascule son modal de prévisualisation
 * sur un autre document de la variante éditée. D'où l'injection par contexte plutôt qu'un import
 * direct, de toute façon interdit par `boundaries/element-types` (`shared` ne peut importer que
 * `shared` et `lib`).
 *
 * L'absence de provider est un cas **normal**, pas une erreur : c'est celui de
 * `EvaluationOverviewView`, l'écran de synopsis d'avant-démarrage, où aucune variante n'est
 * encore tirée et où il n'existe donc pas de document à ouvrir. Les liens inter-documents y
 * rendent un libellé inerte.
 *
 * Le provider vit dans `DocumentLinkProvider.tsx` — un module qui exporte à la fois un
 * composant et un hook déclencherait `react-refresh/only-export-components`.
 */

import { createContext, useContext } from 'react';
import type { DocumentLinkCandidate } from './document-link';

export interface DocumentLinkHandlers {
  /** Résout une cible de lien vers un document ouvrable ; `null` = hors du périmètre courant. */
  resolve: (target: string) => DocumentLinkCandidate | null;
  /** Ouvre le document résolu dans le viewer. */
  open: (documentKey: string) => void;
}

export const DocumentLinkContext = createContext<DocumentLinkHandlers | null>(null);

/** Handlers du contexte, ou `null` hors provider (cf. @remarks du module). */
export function useDocumentLinkHandlers(): DocumentLinkHandlers | null {
  return useContext(DocumentLinkContext);
}
